import { useState } from 'react'
import { router } from '@inertiajs/react'
import { useTranslation } from 'react-i18next'
import { AdminLayout } from '../../../layouts/admin-layout'
import { AppHeader } from '../../../components/app-header'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../../components/ui/card'
import { Button } from '../../../components/ui/button'
import { Input } from '../../../components/ui/input'
import { Label } from '../../../components/ui/label'
import { Link } from '@inertiajs/react'
import { Eye, EyeOff } from 'lucide-react'

interface SystemSecret {
  id: number
  key: string
  value: string
  created_at: string
  updated_at: string
}

interface DevInstance {
  id: number
  name: string
}

interface AdminDevInstanceSystemSecretsProps {
  dev_instance: DevInstance
  system_secrets: SystemSecret[]
  auth: {
    user: {
      id: number
      name: string
      email: string
      admin: boolean
      super_admin?: boolean
    }
  }
  flash?: {
    success?: string
    error?: string
    notice?: string
  }
}

function SystemSecrets({ dev_instance, system_secrets }: AdminDevInstanceSystemSecretsProps) {
  const { t } = useTranslation('admin')
  const [newKey, setNewKey] = useState('')
  const [newValue, setNewValue] = useState('')
  const [editingId, setEditingId] = useState<number | null>(null)
  const [editKey, setEditKey] = useState('')
  const [editValue, setEditValue] = useState('')
  const [originalKey, setOriginalKey] = useState('')
  const [visibleValues, setVisibleValues] = useState<Set<number>>(new Set())
  const [vpsSecretsContent, setVpsSecretsContent] = useState<string | null>(null)
  const [loadingVpsSecrets, setLoadingVpsSecrets] = useState(false)
  const [vpsSecretsError, setVpsSecretsError] = useState<string | null>(null)

  const handleCreate = (e: React.FormEvent) => {
    e.preventDefault()

    console.log('Creating system secret:', { key: newKey, value: newValue })

    router.post(`/admin/dev_instances/${dev_instance.id}/system_secrets`, {
      key: newKey,
      value: newValue
    }, {
      onSuccess: () => {
        setNewKey('')
        setNewValue('')
      },
      onError: (errors) => {
        console.error('Error creating system secret:', errors)
      }
    })
  }

  const handleUpdate = (_id: number) => {
    router.patch(`/admin/dev_instances/${dev_instance.id}/system_secrets`, {
      old_key: originalKey,
      new_key: editKey,
      value: editValue
    }, {
      onSuccess: () => {
        setEditingId(null)
        setEditKey('')
        setEditValue('')
        setOriginalKey('')
      },
      onError: (errors) => {
        console.error('Error updating system secret:', errors)
      }
    })
  }

  const handleDelete = (_id: number, key: string) => {
    if (confirm(t('devInstances.systemSecrets.deleteConfirm', { key }))) {
      // Use query parameter for DELETE request
      router.visit(`/admin/dev_instances/${dev_instance.id}/system_secrets?key=${encodeURIComponent(key)}`, {
        method: 'delete',
        preserveState: false,
        onError: (errors) => {
          console.error('Error deleting system secret:', errors)
        }
      })
    }
  }

  const startEdit = (variable: SystemSecret) => {
    setEditingId(variable.id)
    setOriginalKey(variable.key)
    setEditKey(variable.key)
    setEditValue(variable.value)
  }

  const cancelEdit = () => {
    setEditingId(null)
    setOriginalKey('')
    setEditKey('')
    setEditValue('')
  }

  const toggleValueVisibility = (id: number) => {
    setVisibleValues(prev => {
      const newSet = new Set(prev)
      if (newSet.has(id)) {
        newSet.delete(id)
      } else {
        newSet.add(id)
      }
      return newSet
    })
  }

  const handleFetchVpsSecrets = async () => {
    setLoadingVpsSecrets(true)
    setVpsSecretsError(null)

    try {
      const response = await fetch(`/admin/dev_instances/${dev_instance.id}/fetch_vps_secrets`)
      const data = await response.json()

      if (!response.ok) {
        setVpsSecretsError(data.error || 'Failed to fetch VPS secrets')
        setVpsSecretsContent(null)
      } else {
        setVpsSecretsContent(data.contents)
        setVpsSecretsError(null)
      }
    } catch (error) {
      setVpsSecretsError('Network error while fetching VPS secrets')
      setVpsSecretsContent(null)
    } finally {
      setLoadingVpsSecrets(false)
    }
  }

  return (
    <>
      <AppHeader breadcrumbs={[{ label: 'Admin', href: '/admin' }, { label: t('devInstances.index.title'), href: '/admin/dev_instances' }, { label: t('devInstances.systemSecrets.title') }]} />

      <div className="flex flex-1 flex-col">
        <div className="@container/main flex flex-1 flex-col gap-2">
          <div className="flex flex-col gap-4 py-4 md:gap-6 md:py-6">
            <div className="px-4 lg:px-6">
        {/* Page Header */}
        <div className="mb-6 flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-foreground">{t('devInstances.systemSecrets.title')}</h1>
            <p className="mt-2 text-sm text-muted-foreground">
              {t('devInstances.systemSecrets.description', { name: dev_instance.name })}
            </p>
          </div>
          <Button variant="outline" asChild>
            <Link href={`/admin/dev_instances/${dev_instance.id}`}>{t('devInstances.systemSecrets.backButton')}</Link>
          </Button>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* System Secrets List */}
          <div className="lg:col-span-2">
            <div className="bg-card rounded-lg border border-border overflow-hidden">
              <div className="px-6 py-4 border-b border-border">
                <h2 className="text-lg font-semibold text-foreground">{t('devInstances.systemSecrets.currentSecrets.title')}</h2>
                <p className="mt-1 text-sm text-muted-foreground">
                  {t('devInstances.systemSecrets.currentSecrets.count', { count: system_secrets.length })}
                </p>
              </div>

              {system_secrets.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-muted">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                          {t('devInstances.systemSecrets.table.key')}
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                          {t('devInstances.systemSecrets.table.value')}
                        </th>
                        <th className="px-6 py-3 text-right text-xs font-medium text-muted-foreground uppercase tracking-wider">
                          {t('devInstances.systemSecrets.table.actions')}
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-card divide-y divide-border">
                      {system_secrets.map((variable) => (
                        <tr key={variable.id} className="hover:bg-muted">
                          {editingId === variable.id ? (
                            <>
                              <td className="px-6 py-4">
                                <Input
                                  type="text"
                                  value={editKey}
                                  onChange={(e) => setEditKey(e.target.value)}
                                  className="font-mono text-sm"
                                />
                              </td>
                              <td className="px-6 py-4">
                                <Input
                                  type="text"
                                  value={editValue}
                                  onChange={(e) => setEditValue(e.target.value)}
                                  className="font-mono text-sm"
                                />
                              </td>
                              <td className="px-6 py-4 text-right">
                                <div className="flex gap-2 justify-end">
                                  <Button size="sm" onClick={() => handleUpdate(variable.id)}>
                                    {t('devInstances.systemSecrets.table.save')}
                                  </Button>
                                  <Button size="sm" variant="outline" onClick={cancelEdit}>
                                    {t('devInstances.systemSecrets.table.cancel')}
                                  </Button>
                                </div>
                              </td>
                            </>
                          ) : (
                            <>
                              <td className="px-6 py-4">
                                <span className="text-sm font-medium text-foreground font-mono">{variable.key}</span>
                              </td>
                              <td className="px-6 py-4">
                                <div className="flex items-center gap-2">
                                  <span className="text-sm text-muted-foreground font-mono">
                                    {visibleValues.has(variable.id) ? variable.value : '••••••••'}
                                  </span>
                                  <button
                                    type="button"
                                    onClick={() => toggleValueVisibility(variable.id)}
                                    className="text-gray-400 hover:text-muted-foreground"
                                  >
                                    {visibleValues.has(variable.id) ? (
                                      <EyeOff className="h-4 w-4" />
                                    ) : (
                                      <Eye className="h-4 w-4" />
                                    )}
                                  </button>
                                </div>
                              </td>
                              <td className="px-6 py-4 text-right">
                                <div className="flex gap-2 justify-end">
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => startEdit(variable)}
                                  >
                                    {t('devInstances.systemSecrets.table.edit')}
                                  </Button>
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    className="text-destructive hover:text-destructive hover:bg-destructive/10"
                                    onClick={() => handleDelete(variable.id, variable.key)}
                                  >
                                    {t('devInstances.systemSecrets.table.delete')}
                                  </Button>
                                </div>
                              </td>
                            </>
                          )}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="px-6 py-8">
                  <p className="text-sm text-muted-foreground text-center">
                    {t('devInstances.systemSecrets.currentSecrets.empty')}
                  </p>
                </div>
              )}
            </div>

            {/* VPS Secrets Viewer */}
            <div className="bg-card rounded-lg border border-border overflow-hidden mt-6">
              <div className="px-6 py-4 border-b border-border flex items-center justify-between">
                <div>
                  <h2 className="text-lg font-semibold text-foreground">{t('devInstances.systemSecrets.vpsSecrets.title')}</h2>
                  <p className="mt-1 text-sm text-muted-foreground">
                    {t('devInstances.systemSecrets.vpsSecrets.description')}
                  </p>
                </div>
                <Button
                  onClick={handleFetchVpsSecrets}
                  disabled={loadingVpsSecrets}
                  variant="outline"
                >
                  {loadingVpsSecrets ? t('devInstances.systemSecrets.vpsSecrets.loading') : t('devInstances.systemSecrets.vpsSecrets.viewButton')}
                </Button>
              </div>

              {vpsSecretsError && (
                <div className="px-6 py-4 bg-destructive/10 text-destructive">
                  <p className="text-sm">{vpsSecretsError}</p>
                </div>
              )}

              {vpsSecretsContent && (
                <div className="px-6 py-4">
                  <pre className="text-sm font-mono bg-muted p-4 rounded-md overflow-x-auto whitespace-pre">
                    {vpsSecretsContent}
                  </pre>
                </div>
              )}

              {!vpsSecretsContent && !vpsSecretsError && !loadingVpsSecrets && (
                <div className="px-6 py-8">
                  <p className="text-sm text-muted-foreground text-center">
                    {t('devInstances.systemSecrets.vpsSecrets.empty')}
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* Add New Secret */}
          <div>
            <Card className="shadow-none">
              <CardHeader>
                <CardTitle>{t('devInstances.systemSecrets.addSecret.title')}</CardTitle>
                <CardDescription>{t('devInstances.systemSecrets.addSecret.description')}</CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleCreate} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="key">{t('devInstances.systemSecrets.addSecret.keyLabel')}</Label>
                    <Input
                      id="key"
                      type="text"
                      placeholder={t('devInstances.systemSecrets.addSecret.keyPlaceholder')}
                      value={newKey}
                      onChange={(e) => setNewKey(e.target.value)}
                      required
                      className="font-mono"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="value">{t('devInstances.systemSecrets.addSecret.valueLabel')}</Label>
                    <Input
                      id="value"
                      type="text"
                      placeholder={t('devInstances.systemSecrets.addSecret.valuePlaceholder')}
                      value={newValue}
                      onChange={(e) => setNewValue(e.target.value)}
                      required
                      className="font-mono"
                    />
                  </div>

                  <Button type="submit" className="w-full">
                    {t('devInstances.systemSecrets.addSecret.button')}
                  </Button>
                </form>
              </CardContent>
            </Card>
          </div>
        </div>
            </div>
          </div>
        </div>
      </div>
    </>
  )
}




SystemSecrets.layout = (page: React.ReactNode) => <AdminLayout>{page}</AdminLayout>

export default SystemSecrets
